import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Building2,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FundDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investAmount, setInvestAmount] = useState('');
  const [investing, setInvesting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchFund = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/funds/${id}`);
        setFund(response.data);
        setInvestAmount(response.data.min_investment.toString());
      } catch (error) {
        console.error('Error fetching fund:', error);
        navigate('/funds');
      } finally {
        setLoading(false);
      }
    };
    fetchFund();
  }, [id, navigate]);

  const getFundName = (fund) => {
    if (i18n.language === 'en' && fund.name_en) return fund.name_en;
    if (i18n.language === 'pt' && fund.name_pt) return fund.name_pt;
    return fund.name;
  };

  const getFundDescription = (fund) => {
    if (i18n.language === 'en' && fund.description_en) return fund.description_en;
    if (i18n.language === 'pt' && fund.description_pt) return fund.description_pt;
    return fund.description;
  };

  const getRiskLabel = (level) => {
    const labels = {
      low: t('funds.low'),
      medium: t('funds.medium'),
      high: t('funds.high')
    };
    return labels[level] || level;
  };

  const getGuaranteeConditions = (fund) => {
    if (i18n.language === 'en' && fund.guarantee_conditions_en) return fund.guarantee_conditions_en;
    if (i18n.language === 'pt' && fund.guarantee_conditions_pt) return fund.guarantee_conditions_pt;
    return fund.guarantee_conditions || '';
  };

  const getGuaranteeTypeLabel = (type) => {
    if (i18n.language === 'en') return type === 'full' ? 'Full Guarantee' : type === 'partial' ? 'Partial Guarantee' : 'None';
    if (i18n.language === 'pt') return type === 'full' ? 'Garantia Total' : type === 'partial' ? 'Garantia Parcial' : 'Nenhuma';
    return type === 'full' ? t('funds.guaranteeFull') : type === 'partial' ? t('funds.guaranteePartial') : t('funds.guaranteeNone');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleInvest = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!user.is_kyc_verified) {
      toast.error(t('kyc.required'));
      navigate('/kyc');
      return;
    }

    const amount = parseFloat(investAmount);
    if (isNaN(amount) || amount < fund.min_investment) {
      toast.error(`Minimum investment is ${formatCurrency(fund.min_investment)}`);
      return;
    }

    setInvesting(true);
    try {
      await axios.post(
        `${API_URL}/api/investments`,
        { fund_id: id, amount },
        { withCredentials: true }
      );
      toast.success('Investment successful!');
      setDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Investment failed';
      toast.error(message);
    } finally {
      setInvesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-8 w-32 mb-8 bg-[#141414]" />
          <Skeleton className="h-64 w-full mb-8 bg-[#141414]" />
          <Skeleton className="h-48 w-full bg-[#141414]" />
        </div>
      </div>
    );
  }

  if (!fund) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => navigate('/funds')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>

        {/* Fund Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative h-64 sm:h-80 rounded-sm overflow-hidden mb-8">
            <img
              src={fund.image_url || 'https://images.pexels.com/photos/2275008/pexels-photo-2275008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
              alt={getFundName(fund)}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 text-xs rounded-sm risk-${fund.risk_level}`}>
                      {getRiskLabel(fund.risk_level)}
                    </span>
                    {fund.is_guaranteed && (
                      <span className="bg-[#D4AF37] text-black text-xs px-2 py-1 rounded-sm font-semibold flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {t('funds.guaranteed')}
                      </span>
                    )}
                  </div>
                  <h1 className="font-heading text-2xl sm:text-3xl text-white">
                    {getFundName(fund)}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                <h2 className="text-white font-semibold mb-4">About This Fund</h2>
                <p className="text-zinc-400 leading-relaxed">
                  {getFundDescription(fund)}
                </p>
              </CardContent>
            </Card>

            {/* Details */}
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                <h2 className="text-white font-semibold mb-6">Fund Details</h2>
                <div className="grid grid-cols-2 gap-6">
                  {fund.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-zinc-500 mt-0.5" />
                      <div>
                        <p className="data-label">Location</p>
                        <p className="text-white">{fund.location}</p>
                      </div>
                    </div>
                  )}
                  {fund.property_type && (
                    <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-zinc-500 mt-0.5" />
                      <div>
                        <p className="data-label">Property Type</p>
                        <p className="text-white capitalize">{fund.property_type}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="data-label">{t('funds.totalRaised')}</p>
                      <p className="text-white">{formatCurrency(fund.total_raised)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="data-label">{t('funds.riskLevel')}</p>
                      <p className="text-white">{getRiskLabel(fund.risk_level)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guarantee Information */}
            {fund.is_guaranteed && (
              <Card className="bg-[#D4AF37]/5 border-[#D4AF37]/20 rounded-sm" data-testid="guarantee-info-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-[#D4AF37] font-semibold mb-4">
                        {t('funds.guaranteeInfo')}
                      </h3>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">
                            {t('funds.guaranteeType')}
                          </p>
                          <p className="text-white font-semibold">{getGuaranteeTypeLabel(fund.guarantee_type)}</p>
                        </div>
                        {fund.guarantee_rate && (
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">
                              {t('funds.guaranteeRate')}
                            </p>
                            <p className="text-[#D4AF37] font-semibold text-xl">{fund.guarantee_rate}%</p>
                          </div>
                        )}
                        {fund.guarantee_period && (
                          <div className="col-span-2">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">
                              {t('funds.guaranteePeriod')}
                            </p>
                            <p className="text-white">{fund.guarantee_period}</p>
                          </div>
                        )}
                      </div>
                      {getGuaranteeConditions(fund) && (
                        <div className="pt-3 border-t border-[#D4AF37]/10">
                          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">
                            {t('funds.guaranteeConditions')}
                          </p>
                          <p className="text-zinc-400 text-sm whitespace-pre-line">{getGuaranteeConditions(fund)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risk Warning */}
            <Card className="bg-amber-500/5 border-amber-500/20 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-amber-500 font-semibold mb-2">Risk Warning</h3>
                    <p className="text-zinc-400 text-sm">
                      Investment involves risk. Principal is not guaranteed. Past performance does not guarantee future results. 
                      Please carefully consider your investment objectives before investing.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Investment Card */}
          <div className="lg:col-span-1">
            <Card className="card-presage rounded-sm sticky top-24">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <p className="data-label">{t('funds.targetYield')}</p>
                    <p className="data-value text-4xl text-emerald-500">{fund.target_yield}%</p>
                    <p className="text-zinc-500 text-sm mt-1">Annual</p>
                  </div>

                  {fund.is_guaranteed && fund.guarantee_rate && (
                    <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm" data-testid="guarantee-badge-sidebar">
                      <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mb-1">
                        {getGuaranteeTypeLabel(fund.guarantee_type)}
                      </p>
                      <p className="text-[#D4AF37] text-2xl font-bold">{fund.guarantee_rate}%</p>
                      {fund.guarantee_period && <p className="text-zinc-500 text-xs mt-1">{fund.guarantee_period}</p>}
                    </div>
                  )}

                  {fund.actual_yield && (
                    <div>
                      <p className="data-label">{t('funds.actualYield')}</p>
                      <p className="data-value text-2xl text-white">{fund.actual_yield}%</p>
                    </div>
                  )}

                  <div className="pt-4 border-t border-white/10">
                    <p className="data-label">{t('funds.minInvestment')}</p>
                    <p className="data-value text-2xl text-white">{formatCurrency(fund.min_investment)}</p>
                  </div>

                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm py-6 text-lg"
                        data-testid="invest-button"
                      >
                        {t('funds.invest')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141414] border-white/10 rounded-sm">
                      <DialogHeader>
                        <DialogTitle className="text-white font-heading">
                          {t('funds.invest')} - {getFundName(fund)}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-6 py-4">
                        {!user && (
                          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                            <p className="text-amber-400 text-sm">
                              Please login to invest.
                            </p>
                          </div>
                        )}
                        
                        {user && !user.is_kyc_verified && (
                          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-sm flex items-center gap-3">
                            <Shield className="h-5 w-5 text-amber-500" />
                            <div>
                              <p className="text-amber-400 text-sm font-semibold">{t('kyc.required')}</p>
                              <p className="text-zinc-400 text-xs">{t('kyc.description')}</p>
                            </div>
                          </div>
                        )}

                        <div>
                          <Label className="text-zinc-400">Investment Amount (JPY)</Label>
                          <Input
                            type="number"
                            value={investAmount}
                            onChange={(e) => setInvestAmount(e.target.value)}
                            min={fund.min_investment}
                            step={10000}
                            className="mt-2 bg-[#0A0A0A] border-white/10 text-white rounded-sm focus:border-white/30"
                            data-testid="invest-amount"
                          />
                          <p className="text-zinc-500 text-xs mt-2">
                            Minimum: {formatCurrency(fund.min_investment)}
                          </p>
                        </div>

                        <div className="pt-4 border-t border-white/10">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-zinc-400">Expected Annual Return</span>
                            <span className="data-value text-emerald-500">
                              {formatCurrency(parseFloat(investAmount || 0) * (fund.target_yield / 100))}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Monthly Return</span>
                            <span className="data-value text-white">
                              {formatCurrency(parseFloat(investAmount || 0) * (fund.target_yield / 100) / 12)}
                            </span>
                          </div>
                        </div>

                        <Button
                          className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm"
                          onClick={handleInvest}
                          disabled={investing || !user || !user.is_kyc_verified}
                          data-testid="confirm-invest"
                        >
                          {investing ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : !user ? (
                            'Login to Invest'
                          ) : !user.is_kyc_verified ? (
                            'Complete KYC First'
                          ) : (
                            `${t('common.confirm')} Investment`
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {!user && (
                    <p className="text-zinc-500 text-xs text-center">
                      <button 
                        onClick={() => navigate('/login')} 
                        className="text-white hover:underline"
                      >
                        Login
                      </button>
                      {' '}or{' '}
                      <button 
                        onClick={() => navigate('/register')} 
                        className="text-white hover:underline"
                      >
                        Register
                      </button>
                      {' '}to invest
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundDetailPage;
